import classNames from 'classnames';
import React, { useState } from 'react';
import Button from '../../components/Button';
import PopOver from '../../components/deprecated/PopOver';
import IconButton from '../../components/IconButton';
import { MenuItem } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import arrowRightIcon from '../../icons/non-prefilled/16x16/arrowRight.svg';
import memberIcon from '../../icons/non-prefilled/16x16/member.svg';
import overflowMenuIcon from '../../icons/non-prefilled/16x16/overflowMenu.svg';
import useNavigate from '../../utils/router/useNavigate';
import historyPath from '../../utils/url/historyPath';
import userPath from '../../utils/url/userPath';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import formatHistoryDate from './formatHistoryDate';
import * as styles from './VersionChangesWallAuthorCard.module.scss';

export const AuthorCardSizes = {
  heights: {
    compact: 40,
    normal: 70,
  },
  normalWidth: parseInt(styles.normalWidth, 10),
};

interface VersionChangesAuthorCardMenuProps {
  isAtCurrentVersion: boolean;
  onGoToVersion: () => void;
  onOpenProfile: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const VersionChangesAuthorCardMenu = ({
  isAtCurrentVersion,
  onGoToVersion,
  onOpenProfile,
  onOpenChange,
  open,
}: VersionChangesAuthorCardMenuProps) => {
  return (
    <OverflowMenu
      open={open}
      onOpenChange={onOpenChange}
      button={
        <IconButton aria-label="Actions" className={styles.button}>
          <SVGIcon size="small">{overflowMenuIcon}</SVGIcon>
        </IconButton>
      }
    >
      <MenuItem
        disabled={isAtCurrentVersion}
        icon={arrowRightIcon}
        tooltip={
          isAtCurrentVersion ? 'You are already\non this version' : undefined
        }
        onClick={onGoToVersion}
      >
        Go to version
      </MenuItem>
      <MenuItem icon={memberIcon} onClick={onOpenProfile}>
        Open profile
      </MenuItem>
    </OverflowMenu>
  );
};

const HISTORY_FRAGMENT = gql(`
  fragment VersionChangesWallAuthorCard_history on SlimHistory {
    id
    insertedAt
    versionFrom
    versionTo
  }
`);

const USER_FRAGMENT = gql(`
  fragment VersionChangesWallAuthorCard_user on UserFields {
    id
    ...MagneticWallItemAvatar_user
    ...userPath_user
  }
`);

const POST_FRAGMENT = gql(`
  fragment VersionChangesWallAuthorCard_post on Post {
    id
    ...historyPath_post
  }
`);

interface AuthorCardProps {
  author: DocumentType<typeof USER_FRAGMENT>;
  history: DocumentType<typeof HISTORY_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  focused: boolean;
  currentVersionFrom: number;
  currentVersionTo: number;
  asPopOver: boolean;
  onClick: () => void;
  initialIsPopOverOpen?: boolean;
}

const VersionChangesWallAuthorCard = ({
  author,
  history,
  post,
  focused,
  currentVersionFrom,
  currentVersionTo,
  asPopOver,
  onClick,
  initialIsPopOverOpen,
}: AuthorCardProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const isAtCurrentVersion =
    history.versionFrom === currentVersionFrom &&
    history.versionTo === currentVersionTo;

  const authorDate = (
    <>
      <div className={styles.authorName}>{author.name}</div>
      <div className={styles.date}>{formatHistoryDate(history.insertedAt)}</div>
    </>
  );

  const card = (
    <div className={styles.card}>
      {asPopOver ? (
        <div className={styles.content}>{authorDate}</div>
      ) : (
        <Button
          className={styles.content}
          onClick={onClick}
          testId="versionChangesWallAuthorCardButton"
        >
          <div className={styles.avatar}>
            <MagneticWallItemAvatar user={author} />
          </div>
          {authorDate}
        </Button>
      )}
      <div className={styles.menu}>
        <VersionChangesAuthorCardMenu
          isAtCurrentVersion={isAtCurrentVersion}
          onGoToVersion={() => {
            navigate(historyPath(post, history.versionFrom, history.versionTo));
          }}
          onOpenProfile={() => {
            navigate(userPath(author));
          }}
          open={isMenuOpen}
          onOpenChange={setIsMenuOpen}
        />
      </div>
    </div>
  );

  const [isPopOverOpen, setIsPopOverOpen] = useState(
    initialIsPopOverOpen ?? false,
  );
  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.isFocused]: focused,
        [styles.asPopOver]: asPopOver,
        [styles.isMenuOpen]: isMenuOpen,
      })}
    >
      {asPopOver ? (
        <PopOver
          placement="left"
          trigger="click"
          arrow={<PopOver.Container.Arrow small />}
          modifiers={[
            { name: 'offset', options: { offset: [10, 8] } },
            { name: 'preventOverflow', enabled: false },
          ]}
          open={isPopOverOpen}
          onOpenChange={setIsPopOverOpen}
          preventCloseOnContentClick
          popOver={
            <div className={styles.popOver}>
              <PopOver.Container noPadding noMargin width="auto">
                {card}
              </PopOver.Container>
            </div>
          }
        >
          <Button onClick={onClick}>
            <MagneticWallItemAvatar user={author} />
          </Button>
        </PopOver>
      ) : (
        card
      )}
    </div>
  );
};

export default VersionChangesWallAuthorCard;
